/**
 * file-browser-store — persists recent locations and user favorites
 * in localStorage for the file browser sidebar.
 */

const STORAGE_KEY = "magic-md:file-browser";
const MAX_RECENTS = 15;

/** Custom MIME type for drag-and-drop paths (avoids conflict with reorder drags) */
export const DRAG_MIME_PATH = "application/x-magic-md-path";

/* ── Types ── */

export interface FileBrowserBookmark {
    path: string;
    name: string;
}

interface StoreData {
    recents: FileBrowserBookmark[];
    favorites: FileBrowserBookmark[];
}

/* ── Internal helpers ── */

function isValidBookmark(entry: unknown): entry is FileBrowserBookmark {
    return (
        typeof entry === "object" &&
        entry !== null &&
        typeof (entry as Record<string, unknown>).path === "string" &&
        typeof (entry as Record<string, unknown>).name === "string"
    );
}

function load(): StoreData {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
            const parsed = JSON.parse(raw) as StoreData;
            return {
                recents: Array.isArray(parsed.recents)
                    ? parsed.recents.filter(isValidBookmark)
                    : [],
                favorites: Array.isArray(parsed.favorites)
                    ? parsed.favorites.filter(isValidBookmark)
                    : [],
            };
        }
    } catch {
        // ignore
    }
    return { recents: [], favorites: [] };
}

function save(data: StoreData): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
        // ignore (e.g. quota exceeded)
    }
}

/* ── Recents ── */

export function getRecents(): FileBrowserBookmark[] {
    return load().recents;
}

export function addRecent(path: string): void {
    const data = load();
    const name =
        path
            .split(/[/\\]/)
            .filter(Boolean)
            .pop() ?? path;
    data.recents = [
        { path, name },
        ...data.recents.filter((r) => r.path !== path),
    ].slice(0, MAX_RECENTS);
    save(data);
}

export function removeRecent(path: string): void {
    const data = load();
    data.recents = data.recents.filter((r) => r.path !== path);
    save(data);
}

export function clearRecents(): void {
    const data = load();
    data.recents = [];
    save(data);
}

/* ── Favorites ── */

export function getFavorites(): FileBrowserBookmark[] {
    return load().favorites;
}

export function addFavorite(path: string, name?: string): void {
    const data = load();
    if (data.favorites.some((f) => f.path === path)) return;
    const label =
        name ??
        path
            .split(/[/\\]/)
            .filter(Boolean)
            .pop() ??
        path;
    data.favorites.push({ path, name: label });
    save(data);
}

export function removeFavorite(path: string): void {
    const data = load();
    data.favorites = data.favorites.filter((f) => f.path !== path);
    save(data);
}

export function reorderFavorites(favorites: FileBrowserBookmark[]): void {
    const data = load();
    data.favorites = favorites;
    save(data);
}
